import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect } from 'preact/hooks';
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-preact';
function Toast({ toast, onRemove }) {
    const [isVisible, setIsVisible] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    useEffect(() => {
        // Trigger enter animation
        const timer = setTimeout(() => setIsVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);
    useEffect(() => {
        if (toast.duration !== 0) {
            const duration = toast.duration || 5000;
            const timer = setTimeout(() => {
                handleRemove();
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [toast.duration]);
    const handleRemove = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsExiting(true);
        setTimeout(() => onRemove(toast.id), 300); // Match exit animation duration
    };
    const getIcon = () => {
        switch (toast.type) {
            case 'success':
                return _jsx(CheckCircle, { className: "w-5 h-5" });
            case 'error':
                return _jsx(AlertCircle, { className: "w-5 h-5" });
            case 'warning':
                return _jsx(AlertTriangle, { className: "w-5 h-5" });
            case 'info':
                return _jsx(Info, { className: "w-5 h-5" });
        }
    };
    const getColors = () => {
        switch (toast.type) {
            case 'success':
                return {
                    bg: 'bg-primary-100 dark:bg-primary-900/80',
                    border: 'border-primary-300 dark:border-primary-600',
                    icon: 'text-primary-700 dark:text-primary-300',
                    title: 'text-primary-900 dark:text-primary-100',
                    message: 'text-primary-800 dark:text-primary-200',
                    button: 'text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200'
                };
            case 'error':
                return {
                    bg: 'bg-red-100 dark:bg-red-900/80',
                    border: 'border-red-300 dark:border-red-600',
                    icon: 'text-red-700 dark:text-red-300',
                    title: 'text-red-900 dark:text-red-100',
                    message: 'text-red-800 dark:text-red-200',
                    button: 'text-red-600 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200'
                };
            case 'warning':
                return {
                    bg: 'bg-yellow-100 dark:bg-yellow-900/80',
                    border: 'border-yellow-300 dark:border-yellow-600',
                    icon: 'text-yellow-700 dark:text-yellow-300',
                    title: 'text-yellow-900 dark:text-yellow-100',
                    message: 'text-yellow-800 dark:text-yellow-200',
                    button: 'text-yellow-600 hover:text-yellow-700 dark:text-yellow-300 dark:hover:text-yellow-200'
                };
            case 'info':
                return {
                    bg: 'bg-primary-100 dark:bg-primary-900/80',
                    border: 'border-primary-300 dark:border-primary-600',
                    icon: 'text-primary-700 dark:text-primary-300',
                    title: 'text-primary-900 dark:text-primary-100',
                    message: 'text-primary-800 dark:text-primary-200',
                    button: 'text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200'
                };
        }
    };
    const colors = getColors();
    return (_jsx("div", { className: `
        transform transition-all duration-300 ease-in-out mb-3
        ${isVisible && !isExiting
            ? 'translate-x-0 opacity-100'
            : 'translate-x-full opacity-0'}
      `, children: _jsx("div", { className: `
          min-w-80 max-w-md w-full ${colors.bg} ${colors.border} border rounded-lg shadow-lg p-4
        `, children: _jsxs("div", { className: "flex items-start", children: [_jsx("div", { className: `flex-shrink-0 ${colors.icon} mt-0.5`, children: getIcon() }), _jsxs("div", { className: "ml-3 flex-1 min-w-0", children: [_jsx("p", { className: `text-sm font-medium ${colors.title} leading-5`, children: toast.title }), _jsx("p", { className: `mt-1 text-sm ${colors.message} leading-relaxed break-words`, children: toast.message })] }), _jsx("div", { className: "ml-4 flex-shrink-0 flex", children: _jsxs("button", { className: `
                inline-flex ${colors.button} focus:outline-none focus:ring-2 
                focus:ring-offset-2 focus:ring-primary-500 rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/5
              `, onClick: handleRemove, children: [_jsx("span", { className: "sr-only", children: "Close" }), _jsx(X, { className: "w-4 h-4" })] }) })] }) }) }));
}
export function ToastContainer({ toasts, onRemoveToast }) {
    return (_jsx("div", { className: "fixed top-4 right-4 z-50 space-y-3 max-w-md", style: { zIndex: 9999 }, children: toasts.map((toast) => (_jsx(Toast, { toast: toast, onRemove: onRemoveToast }, toast.id))) }));
}
